// Cuts a document's comment thread at its approval moments (submission_approvals,
// migration 0094), so a compliant document starts with a fresh conversation.
// Lives in @l2u/shared because the API and both portals must slice a thread the
// same way, and the API is a Worker that cannot import @l2u/ui.
//
// A message is placed by when the VENDOR could first see it (vendorVisibleAt):
// an admin comment at publish, so a comment drafted before an approval but sent
// in the Notify after it opens the new conversation; a vendor comment at
// creation. An unpublished admin draft has no placement and is always current.
// ISO strings compare with `<`/`<=`; a message exactly at an approval moment
// belongs to that approval's slice.

import { vendorVisibleAt } from './documentRound.ts';
import { isDocumentsTabDocument, type DocumentsTabQuestion } from './documentsTab.ts';

export type ThreadPartition<T> = {
  /** Up to the first approval (exclusive). Everything when there are no approvals. */
  original: T[];
  /** One entry per gap between consecutive approvals: earlier[i] is [approvals[i], approvals[i+1]). Length approvals.length - 1 (0 when ≤ 1 approval); entries may be empty. */
  earlier: T[][];
  /** From the latest approval (inclusive), plus every unpublished admin draft. Empty when there are no approvals. */
  current: T[];
};

/** A thread row as the partition reads it. `published_at` may be absent (SubmissionComment declares it optional); absent reads exactly like null. */
export type PartitionRow = {
  author_type: 'admin' | 'vendor';
  created_at: string;
  published_at?: string | null;
};

/** Where a message sits in time for slicing: vendorVisibleAt (admin → published_at, null for a draft or when absent; vendor → created_at). */
export function placementAt(row: PartitionRow): string | null {
  return vendorVisibleAt({
    author_type: row.author_type,
    created_at: row.created_at,
    published_at: row.published_at ?? null,
  });
}

/** Cuts a thread at its approval moments. `approvals` is sorted ascending on a copy. Input order is preserved inside each slice. The three slices always partition the input. */
export function partitionThread<T extends PartitionRow>(
  messages: readonly T[],
  approvals: readonly string[],
): ThreadPartition<T> {
  const sorted = [...approvals].sort();
  if (sorted.length === 0) return { original: [...messages], earlier: [], current: [] };

  const last = sorted.length - 1;
  const original: T[] = [];
  const earlier: T[][] = Array.from({ length: last }, () => []);
  const current: T[] = [];
  for (const m of messages) {
    const at = placementAt(m);
    if (at === null) {
      current.push(m);
      continue;
    }
    // The last approval at or before the message (-1 = before every approval).
    let i = -1;
    while (i < last && sorted[i + 1]! <= at) i++;
    if (i === -1) original.push(m);
    else if (i === last) current.push(m);
    else earlier[i]!.push(m);
  }
  return { original, earlier, current };
}

/**
 * The slice a Documents-tab screen shows: `partitionThread(...).current`, or every message when `approvals` is null (not a partitioned document).
 * Pass `documentsTabApprovals`'s result (null or a non-empty sorted list). An empty list means "partitioned, nothing current" and hides every message.
 */
export function currentConversation<T extends PartitionRow>(
  messages: readonly T[],
  approvals: readonly string[] | null,
): T[] {
  if (approvals === null) return [...messages];
  return partitionThread(messages, approvals).current;
}

export type ApprovedSubmission = {
  /** LIVE status (the vendor payload's `status`; the admin payload's `live_status`). */
  status: string;
  reverify_requested_at?: string | null;
  approvals?: readonly string[] | null;
};

/** The approval moments to partition by, or null when the whole thread shows as today: not a Documents-tab document (live status), or no recorded approval. */
export function documentsTabApprovals(
  question: DocumentsTabQuestion | undefined | null,
  submission: ApprovedSubmission | undefined | null,
): string[] | null {
  if (!submission || !isDocumentsTabDocument(question, submission)) return null;
  const approvals = submission.approvals;
  if (!approvals || approvals.length === 0) return null;
  return [...approvals].sort();
}
